/*
 * <product-form>: adds the product to the cart.
 * On the product page it also collects lines from offer/add-on/gift elements in the same product root
 * ([data-cart-contributor] elements exposing getCartLines(); see product-page.js), validates required
 * personalization fields, and supports "skip cart" (straight to checkout) per form or per button.
 * Without contributors it posts the form like Dawn (keeps selling plans, gift card fields, etc.).
 */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer') && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      get root() {
        return this.closest('[data-product-root]');
      }

      collectLines() {
        const root = this.root;
        let replace = null;
        const extra = [];
        if (!root) return { replace, extra };
        root.querySelectorAll('[data-cart-contributor]').forEach((el) => {
          if (typeof el.getCartLines !== 'function') return;
          if (el.dataset.form && el.dataset.form !== this.form.getAttribute('id')) return;
          const result = el.getCartLines();
          if (!result || !result.lines || !result.lines.length) return;
          if (result.replaceMain) replace = result.lines;
          else extra.push(...result.lines);
        });
        return { replace, extra };
      }

      sectionsToRender(skipCart) {
        if (!this.cart || skipCart || !this.cart.getSectionsToRender) return [];
        return this.cart.getSectionsToRender().map((section) => section.id);
      }

      formRequest(skipCart) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        const formData = new FormData(this.form);
        const sections = this.sectionsToRender(skipCart);
        if (sections.length) {
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;
        return fetch(`${routes.cart_add_url}`, config).then((r) => r.json());
      }

      jsonRequest(replace, extra, skipCart) {
        const formData = new FormData(this.form);
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string' && value.trim() !== '') properties[match[1]] = value;
        }
        const sellingPlan = formData.get('selling_plan');
        const decorate = (line) => {
          const out = { ...line };
          if (Object.keys(properties).length) out.properties = { ...properties, ...(line.properties || {}) };
          if (sellingPlan) out.selling_plan = sellingPlan;
          return out;
        };
        const main = replace || [{ id: Number(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 }];
        const items = main.map(decorate).concat(extra);
        return fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: this.sectionsToRender(skipCart), sections_url: window.location.pathname }),
        })
          .then((r) => r.json())
          .then((data) => (data.items ? { ...data.items[0], sections: data.sections } : data));
      }

      setLoading(button, loading) {
        if (!button) return;
        button.classList.toggle('loading', loading);
        if (loading) button.setAttribute('aria-disabled', true);
        else if (!this.error) button.removeAttribute('aria-disabled');
        const spinner = button.querySelector('.loading-overlay__spinner') || this.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.toggle('hidden', !loading);
        document.dispatchEvent(new CustomEvent('vesper:product-form', { detail: { formId: this.form.getAttribute('id'), loading } }));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const submitter = evt.submitter && evt.submitter.type === 'submit' ? evt.submitter : this.submitButton;
        if (!submitter || submitter.getAttribute('aria-disabled') === 'true') return;

        const controller = window.VesperProduct && window.VesperProduct.controllerFor(this);
        if (controller && !controller.validate(true)) return;

        const skipCart = submitter.dataset.skipCart === 'true' || this.dataset.skipCart === 'true';
        this.handleErrorMessage();
        this.setLoading(submitter, true);
        if (this.cart && !skipCart) this.cart.setActiveElement(document.activeElement);

        const { replace, extra } = this.collectLines();
        const request = replace || extra.length ? this.jsonRequest(replace, extra, skipCart) : this.formRequest(skipCart);

        request
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description || response.message);
              this.error = true;
              return;
            }
            this.error = false;
            if (skipCart) {
              window.location.href = '/checkout';
              return;
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => this.cart.renderContents(response));
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => console.error(e))
          .finally(() => {
            this.setLoading(submitter, false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          });
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }
    }
  );
}
